/**
 * Painel "Pedido de Terraform" do chamado — onde quem aprova decide (spec
 * 2026-10-01 D1–D3, TASK-12146).
 *
 * Até aqui o pedido em `aguardando_aprovacao` não tinha tela: a rota
 * `POST /aprovar` existia e nenhum front a chamava (zero aprovações em
 * produção). O aviso aos aprovadores aponta para `/chamados/<id>`, e é aqui
 * que o clique acontece.
 *
 * Quem decide se o botão aparece é o servidor (`aprovacao.pode_aprovar`); o
 * painel nunca deduz papel. Recusar exige motivo — o servidor recusa sem ele
 * (422), e o painel nem deixa enviar.
 *
 * Autoaprovação (`aprovacao.autoaprovacao`, TASK-12153): o solicitante que é
 * Administrador aprova o próprio pedido com justificativa obrigatória, e não
 * recusa — o servidor aplica as mesmas regras.
 *
 * Completar a criação (`pode_completar`, TASK-12154): a criação que parou em
 * `apply_parcial` ganha o botão que cria o pedido novo. O painel passa a ele
 * sozinho, porque a rota do chamado prefere o pedido não terminal. A criação
 * sem rede (TASK-12167) e o ajuste livre em `apply_parcial` ("Reaplicar a
 * partir da master", consoleai#26379) usam o mesmo botão (`CompletarDoPedido`).
 *
 * Execução (TASK-12155): o log ao vivo do plan e do apply, com "N de M
 * mudanças feitas" e a linha `Error:`, é o `PainelDeExecucao` do chat.
 */
import { Anchor, Badge, Group, Paper, Stack, Text } from '@mantine/core'
import { IconAdjustments } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getPedidoDoChamado, type PedidoDoCartao } from '@/api/terraformRunner'
import { PainelDeExecucao, totalDoPlan } from '@/pages/chat/components/PainelDeExecucao'
import { MotivoDoPedido } from '@/pages/chat/components/MotivoDoPedido'
import { PausaDaFila } from '@/pages/chat/components/PausaDaFila'
import { LinhaDoTempoDoPedido } from '@/pages/chat/components/LinhaDoTempoDoPedido'
import { DecisaoDoPedido, NovosDesdeOAprovado } from './DecisaoDoPedido'
import { CompletarDoPedido } from './CompletarDoPedido'
import { JanelaDeConstrucao } from './JanelaDeConstrucao'

const BARLOW = '"Barlow", Arial, sans-serif'
const MONO = '"JetBrains Mono", monospace'

interface TerraformPedidoPanelProps {
  taskRequestId: number
}

export function TerraformPedidoPanel({ taskRequestId }: TerraformPedidoPanelProps) {
  const qc = useQueryClient()
  const chave = ['terraform-pedido-do-chamado', taskRequestId]
  const { data: pedido } = useQuery<PedidoDoCartao>({
    queryKey: chave,
    queryFn: () => getPedidoDoChamado(taskRequestId),
    retry: false,
    // O estado anda sozinho (plan, MR, apply): reler enquanto vivo.
    refetchInterval: (q) => (q.state.data && !q.state.data.terminal ? 10_000 : false),
  })

  // 404 = chamado sem pedido, ou quem vê não o lê: nada a mostrar.
  if (!pedido) return null

  const frase = pedido.linha_do_tempo?.em_curso?.frase ?? null
  // O rótulo do estado vem da linha do tempo (`linha_do_tempo.py`): uma fonte
  // só para o cartão do chat e para este painel.
  const rotuloDoEstado = pedido.linha_do_tempo?.etapas
    ?.find((e) => e.estado === pedido.estado)?.rotulo ?? pedido.estado
  const novos = pedido.estado === 'aguardando_aprovacao' ? (pedido.novos_desde_o_aprovado ?? []) : []

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Group gap="xs" mb="xs">
        <IconAdjustments size={14} />
        <Text size="sm" fw={500} style={{ fontFamily: BARLOW }}>
          Pedido de Terraform #{pedido.id}
        </Text>
        <Text size="xs" style={{ fontFamily: MONO }}>{pedido.stage_key}</Text>
        <Badge size="xs" variant="light" color={pedido.terminal ? 'gray' : 'inovareTeal'}>
          {rotuloDoEstado}
        </Badge>
      </Group>

      <Stack gap={6}>
        {frase && <Text size="sm" style={{ fontFamily: BARLOW }}>{frase}</Text>}
        {pedido.linha_plan && (
          <Text size="sm" style={{ fontFamily: MONO }}>{pedido.linha_plan}</Text>
        )}
        {pedido.terminal && pedido.motivo_terminal && (
          <MotivoDoPedido texto={pedido.motivo_terminal} dimmed />
        )}
        {/* A mesma linha do tempo do cartão do chat e da tela de WAF: quem
            abre o chamado vê em que etapa o pedido está, de quem se espera,
            até quando, e — se parou — o que fazer. */}
        {/* Adendo I2 (spec 2026-10-15 §5.4): este pedido pausou a fila do stage. */}
        {pedido.pausa && <PausaDaFila pausa={pedido.pausa} />}
        <LinhaDoTempoDoPedido linha={pedido.linha_do_tempo} mrUrl={pedido.mr_url} />
        {/* O andamento ao vivo (TASK-12155): o mesmo painel do cartão do chat,
            lido pelo chamado, e já aberto durante o apply. */}
        <PainelDeExecucao
          pedidoId={pedido.id}
          disponivel={pedido.andamento_disponivel}
          viaChamado={taskRequestId}
          abertoDeInicio={pedido.estado === 'aplicando'}
          totalDeMudancas={totalDoPlan(pedido.linha_plan)}
          acoes={pedido.acoes}
        />
        {pedido.completa_pedido_id != null && (
          <Text size="sm" style={{ fontFamily: BARLOW }}>
            Este pedido retoma a criação que parou no pedido #{pedido.completa_pedido_id}.
          </Text>
        )}
        <NovosDesdeOAprovado enderecos={novos} />
        {pedido.mr_url && (
          <Anchor href={pedido.mr_url} target="_blank" rel="noreferrer" size="sm"
                  style={{ fontFamily: BARLOW }}>
            Ver merge request
          </Anchor>
        )}
        <DecisaoDoPedido pedido={pedido}
                         onDecidido={() => void qc.invalidateQueries({ queryKey: chave })} />
        <CompletarDoPedido pedido={pedido}
                           onFeito={() => void qc.invalidateQueries({ queryKey: chave })} />
        {pedido.janela && (
          <JanelaDeConstrucao janela={pedido.janela}
                              onMudou={() => void qc.invalidateQueries({ queryKey: chave })} />
        )}
      </Stack>

    </Paper>
  )
}
